import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PlatformCredentialsManager } from '../components/PlatformCredentialsManager';
import { DeploymentCheckPanel } from '../components/DeploymentCheckPanel';
import { RescueScheduledMediaPanel } from '../components/RescueScheduledMediaPanel';
import { AdminSupportChatsPanel } from '../components/AdminSupportChatsPanel';
import { StripeSyncPanel } from '../components/StripeSyncPanel';
import { ImageCreditWeightsPanel } from '../components/ImageCreditWeightsPanel';
import { SendGridSettingsPanel } from '../components/SendGridSettingsPanel';
import { FeatureFlagsPanel } from '../components/FeatureFlagsPanel';
import { SSOApplicationsSection } from '../components/SSOApplicationsSection';
import { SiteSettingsSection } from '../components/SiteSettingsSection';
import { TicketDetailDialog } from './SupportPage';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Users, DollarSign, FileText, TrendingUp, Settings,
  Search, MoreVertical, Ban, Crown, Zap, Clock,
  Edit2, Trash2, Eye, Loader2, CreditCard, Check, X,
  RefreshCw, Shield, KeyRound, Copy, Plus, ArrowUp, ArrowDown, GripVertical,
  Sparkles, Mail, LifeBuoy, UserCog
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

export const AdminDashboard = () => {
  const { api, user: currentUser, impersonate } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('all');

  // Modal states
  const [editUserModal, setEditUserModal] = useState(null);
  const [editTierModal, setEditTierModal] = useState(null);
  const [stripeSettingsModal, setStripeSettingsModal] = useState(false);
  const [openaiSettingsModal, setOpenaiSettingsModal] = useState(false);
  const [viewUserModal, setViewUserModal] = useState(null);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [resetPwResult, setResetPwResult] = useState(null);
  const [resetPwForm, setResetPwForm] = useState({ new_password: '', send_email: true });
  const [resetPwLoading, setResetPwLoading] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createUserResult, setCreateUserResult] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, tiersRes, transRes, settingsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users?limit=100'),
        api.get('/admin/tiers'),
        api.get('/admin/transactions?limit=50'),
        api.get('/admin/settings')
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setTiers(tiersRes.data.tiers);
      setTransactions(transRes.data.transactions);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    try {
      await api.put(`/admin/users/${editUserModal.id}`, {
        is_banned: editUserModal.is_banned,
        subscription_tier: editUserModal.subscription_tier,
        subscription_status: editUserModal.subscription_status,
        role: editUserModal.role ?? '',
      });
      toast.success('User updated successfully');
      setEditUserModal(null);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure? This will delete all user data.')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  /** One-click ban/unban (disable/enable). Uses the existing PUT endpoint so it
   * stays consistent with the "Edit User" modal. */
  const handleToggleBan = async (user) => {
    const nextBanned = !user.is_banned;
    const verb = nextBanned ? 'disable' : 'enable';
    if (!window.confirm(`${nextBanned ? 'Disable' : 'Enable'} ${user.email}?${nextBanned ? ' They won\'t be able to sign in until re-enabled.' : ''}`)) return;
    try {
      await api.put(`/admin/users/${user.id}`, { is_banned: nextBanned });
      toast.success(`Account ${nextBanned ? 'disabled' : 'enabled'}`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${verb} account`);
    }
  };

  /** Sends the user the standard "reset your password" email without the
   * admin ever seeing the plaintext. */
  const handleSendResetLink = async (user) => {
    if (!window.confirm(`Email a password reset link to ${user.email}?`)) return;
    try {
      const res = await api.post(`/admin/users/${user.id}/send-reset-link`);
      if (res.data.email_sent) {
        toast.success(`Reset link sent to ${user.email}`);
      } else if (res.data.reset_url) {
        // Email delivery failed — surface the link so the admin can share it.
        await navigator.clipboard.writeText(res.data.reset_url);
        toast.success('Email delivery failed — reset link copied to clipboard');
      } else {
        toast.success('Reset link created');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset link');
    }
  };

  /** Admin-create flow. `payload`:
   *   { email, name?, password?, subscription_tier?, is_admin?, send_reset_link? } */
  const handleCreateUser = async (payload) => {
    try {
      const res = await api.post('/admin/users', payload);
      setCreateUserResult(res.data);
      fetchAllData();
      if (res.data.generated_password) {
        toast.success(`User created. Temp password copied to the result panel.`);
      } else if (res.data.reset_link_sent) {
        toast.success(`User created. Password-set link emailed to ${payload.email}.`);
      } else {
        toast.success(`User created.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleAdminResetPassword = async () => {
    if (!resetPwUser) return;
    setResetPwLoading(true);
    setResetPwResult(null);
    try {
      const payload = {
        send_email: resetPwForm.send_email,
        ...(resetPwForm.new_password ? { new_password: resetPwForm.new_password } : {}),
      };
      const res = await api.post(`/admin/users/${resetPwUser.id}/reset-password`, payload);
      setResetPwResult(res.data);
      toast.success(
        res.data.email_sent
          ? 'Password reset and emailed to user'
          : 'Password reset successfully'
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetPwLoading(false);
    }
  };

  const closeResetPwModal = () => {
    setResetPwUser(null);
    setResetPwResult(null);
    setResetPwForm({ new_password: '', send_email: true });
  };

  /** Mint an impersonation JWT for the target user and route to their dashboard.
   *  The admin's original token is stashed in localStorage so the
   *  ImpersonationBanner can swap back on "Return to admin". */
  const handleImpersonate = async (target) => {
    if (target.id === currentUser?.id) {
      toast.error("You're already logged in as this user.");
      return;
    }
    const ok = window.confirm(
      `Log in as ${target.name || target.email}?\n\n` +
      `You'll see and control the app exactly as they do. ` +
      `Click "Return to admin" in the orange banner at the top to come back.\n\n` +
      `This action is logged.`
    );
    if (!ok) return;
    try {
      await impersonate(target.id);
      toast.success(`Now viewing as ${target.name || target.email}`);
      window.location.href = '/dashboard';
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  const handleUpdateTier = async () => {
    try {
      // Validate features: trim + drop empties
      const cleanFeatures = (editTierModal.features || [])
        .map((f) => (f || '').trim())
        .filter((f) => f.length > 0);

      // Posts per month: support 'Unlimited' UX → -1, else require non-negative integer
      const ppmRaw = String(editTierModal.posts_per_month ?? '').trim();
      let ppm;
      if (ppmRaw.toLowerCase() === 'unlimited' || ppmRaw === '-1') {
        ppm = -1;
      } else {
        const n = parseInt(ppmRaw, 10);
        if (Number.isNaN(n) || n < 0) {
          toast.error('Posts per month must be a positive number or "Unlimited"');
          return;
        }
        ppm = n;
      }

      // AI Images per month: same UX as posts (blank → keep current). -1 = unlimited.
      const aiRaw = String(editTierModal.ai_images_per_month ?? '').trim();
      let aipm;
      if (aiRaw === '') {
        aipm = undefined; // skip — keep DB value
      } else if (aiRaw.toLowerCase() === 'unlimited' || aiRaw === '-1') {
        aipm = -1;
      } else {
        const n = parseInt(aiRaw, 10);
        if (Number.isNaN(n) || n < 0) {
          toast.error('AI Images per month must be a positive number or "Unlimited"');
          return;
        }
        aipm = n;
      }

      // Price: required for paid tiers
      let priceNum;
      if (editTierModal.is_trial) {
        priceNum = 0;
      } else {
        priceNum = parseFloat(editTierModal.price);
        if (Number.isNaN(priceNum) || priceNum < 0) {
          toast.error('Enter a valid price');
          return;
        }
      }

      const durationNum = parseInt(editTierModal.duration_days, 10);
      if (Number.isNaN(durationNum) || durationNum <= 0) {
        toast.error('Duration must be a positive number of days');
        return;
      }

      if (!editTierModal.name?.trim()) {
        toast.error('Name cannot be empty');
        return;
      }

      const payload = {
        name: editTierModal.name.trim(),
        price: priceNum,
        posts_per_month: ppm,
        duration_days: durationNum,
        features: cleanFeatures,
      };
      if (aipm !== undefined) payload.ai_images_per_month = aipm;
      // ── Discount fields (always send so explicit "remove" → 0 round-trips correctly) ──
      const rawPct = String(editTierModal.discount_percent ?? '').trim();
      if (rawPct === '' || rawPct === '0') {
        payload.discount_percent = 0;        // backend treats 0 as "clear discount"
      } else {
        const n = parseFloat(rawPct);
        if (Number.isNaN(n) || n < 0 || n > 100) {
          toast.error('Discount % must be between 0 and 100');
          return;
        }
        payload.discount_percent = n;
        payload.discount_label = (editTierModal.discount_label || '').trim() || null;
        // discount_ends_at is an ISO datetime string ("" or null → forever)
        payload.discount_ends_at = (editTierModal.discount_ends_at || '').trim() || null;
      }
      // ── Promo trial fields ──
      payload.promo_trial_enabled = !!editTierModal.promo_trial_enabled;
      if (payload.promo_trial_enabled) {
        payload.promo_trial_price = Number(editTierModal.promo_trial_price ?? 1) || 1;
        payload.promo_trial_days = Number(editTierModal.promo_trial_days ?? 5) || 5;
        payload.promo_trial_post_cap = Number(editTierModal.promo_trial_post_cap ?? 10) || 10;
        payload.promo_trial_scheduled_cap = Number(editTierModal.promo_trial_scheduled_cap ?? 10) || 10;
      }
      await api.put(`/admin/tiers/${editTierModal.id}`, payload);
      toast.success('Tier updated successfully');
      setEditTierModal(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tier');
    }
  };

  // Feature list helpers — used inside the edit modal.
  const updateFeature = (idx, value) => {
    setEditTierModal((cur) => {
      const next = [...(cur.features || [])];
      next[idx] = value;
      return { ...cur, features: next };
    });
  };
  const addFeature = () => {
    setEditTierModal((cur) => ({ ...cur, features: [...(cur.features || []), ''] }));
  };
  const removeFeature = (idx) => {
    setEditTierModal((cur) => {
      const next = [...(cur.features || [])];
      next.splice(idx, 1);
      return { ...cur, features: next };
    });
  };
  const moveFeature = (idx, dir) => {
    setEditTierModal((cur) => {
      const next = [...(cur.features || [])];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return cur;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...cur, features: next };
    });
  };

  // The four default tier ids the backend seeds + never deletes. We mirror
  // them client-side so the UI can hide the Delete button instead of letting
  // the user click it and eat a 400.
  const DEFAULT_TIER_IDS = new Set(['free', 'starter', 'growth', 'pro']);

  /** Flip archived flag. Archived plans disappear from /subscription/tiers
   * (the public pricing page) but existing subscribers keep working. */
  const handleToggleTierArchived = async (tier) => {
    try {
      await api.put(`/admin/tiers/${tier.id}`, { archived: !tier.archived });
      toast.success(
        tier.archived
          ? `${tier.name} enabled — visible on signup page`
          : `${tier.name} disabled — hidden from new signups`
      );
      fetchAllData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update plan');
    }
  };

  /** Permanently delete a plan. Backend rejects deletion of the 4 defaults
   * and any plan with active subscribers — surface those errors verbatim. */
  const handleDeleteTier = async (tier) => {
    if (DEFAULT_TIER_IDS.has(tier.id)) {
      toast.error('Default plans can\'t be deleted — use Disable instead.');
      return;
    }
    const ok = window.confirm(
      `Permanently delete "${tier.name}"?\n\n` +
      'This cannot be undone. Existing subscribers will be left without a plan ' +
      'unless they\'ve been migrated. The backend will refuse the delete if any ' +
      'users are still on this plan.'
    );
    if (!ok) return;
    try {
      await api.delete(`/admin/tiers/${tier.id}`);
      toast.success(`${tier.name} deleted`);
      fetchAllData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete plan');
    }
  };

  const handleSaveStripeSettings = async (formData) => {
    try {
      await api.put('/admin/settings/stripe', formData);
      toast.success('Stripe settings saved');
      setStripeSettingsModal(false);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleSaveOpenAISettings = async ({ key, clear }) => {
    try {
      const body = clear ? { openai_api_key: '' } : { openai_api_key: key };
      const res = await api.put('/admin/settings/openai', body);
      toast.success(res.data?.message || 'OpenAI settings saved');
      setOpenaiSettingsModal(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save OpenAI key');
    }
  };

  const handleVerifyOpenAI = async () => {
    try {
      const res = await api.post('/admin/settings/openai/verify');
      if (res.data?.ok) {
        const imgs = (res.data.image_models || []).join(', ') || 'none';
        toast.success(`Connection OK — ${res.data.models_total} models. Image models: ${imgs}`);
      } else {
        toast.error(`Verification failed: ${res.data?.detail || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}`);
      setViewUserModal(response.data);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTier = filterTier === 'all' || user.subscription_tier === filterTier;
    return matchesSearch && matchesTier;
  });

  // Internal (hidden) plan ids — used to show a distinct badge and mark tiers.
  const internalTierIds = new Set(tiers.filter(t => t.internal).map(t => t.id));

  const getTierIcon = (tierId) => {
    const icons = { free_trial: Clock, pro: Zap, enterprise: Crown };
    const Icon = icons[tierId] || Clock;
    return <Icon className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header — stacks on mobile so the title + subtitle don't get squeezed by the Refresh button. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-indigo-600" />
            Admin Dashboard
          </h1>
          <p className="text-zinc-500 text-sm sm:text-base">Manage users, subscriptions, and settings</p>
        </div>
        <Button variant="outline" onClick={fetchAllData} data-testid="refresh-btn" className="self-start sm:self-auto">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Tabs container: horizontally scrolls on mobile (9 tabs would overlap
            in a fixed grid), then snaps to a 9-col grid on md+ screens. */}
        <div className="overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0 scrollbar-thin">
          <TabsList className="inline-flex md:grid md:grid-cols-10 md:w-full md:max-w-5xl gap-1 md:gap-0">
            <TabsTrigger value="overview" data-testid="tab-overview" className="whitespace-nowrap">Overview</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="whitespace-nowrap">Users</TabsTrigger>
            <TabsTrigger value="tickets" data-testid="tab-tickets" className="whitespace-nowrap">Tickets</TabsTrigger>
            <TabsTrigger value="aichats" data-testid="tab-aichats" className="whitespace-nowrap">AI Chats</TabsTrigger>
            <TabsTrigger value="tiers" data-testid="tab-tiers" className="whitespace-nowrap">Pricing</TabsTrigger>
            <TabsTrigger value="features" data-testid="tab-features" className="whitespace-nowrap">Features</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="whitespace-nowrap">Settings</TabsTrigger>
            <TabsTrigger value="connections" data-testid="tab-connections" className="whitespace-nowrap">Platforms</TabsTrigger>
            <TabsTrigger value="sso" data-testid="tab-sso" className="whitespace-nowrap">SSO</TabsTrigger>
            <TabsTrigger value="deploy" data-testid="tab-deploy" className="whitespace-nowrap">Deploy</TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-zinc-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-zinc-500">Total Users</p>
                    <p className="text-3xl font-bold text-zinc-900">{stats?.total_users || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center">
                    <Users className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-zinc-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-zinc-500">Active Subscriptions</p>
                    <p className="text-3xl font-bold text-zinc-900">{stats?.active_subscriptions || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-zinc-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-zinc-500">Total Revenue</p>
                    <p className="text-3xl font-bold text-zinc-900">${stats?.total_revenue || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-zinc-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-zinc-500">Total Posts</p>
                    <p className="text-3xl font-bold text-zinc-900">{stats?.total_posts || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-violet-50 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-violet-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Users by Tier */}
          <Card className="border-zinc-200">
            <CardHeader>
              <CardTitle className="text-lg">Users by Subscription</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-4">
                {Object.entries(stats?.users_by_tier || {}).map(([tier, count]) => (
                  <div key={tier} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-50">
                    {getTierIcon(tier)}
                    <div>
                      <p className="font-medium capitalize">{tier.replace('_', ' ')}</p>
                      <p className="text-sm text-zinc-500">{count} users</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          <Card className="border-zinc-200">
            <CardHeader>
              <CardTitle className="text-lg">Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {transactions.slice(0, 5).map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-50">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        tx.payment_status === 'paid' ? 'bg-emerald-100' : 'bg-zinc-100'
                      }`}>
                        {tx.payment_status === 'paid' ? (
                          <Check className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <Clock className="w-4 h-4 text-zinc-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-zinc-900">{tx.user_email || tx.user_id}</p>
                        <p className="text-sm text-zinc-500 capitalize">{tx.tier_id?.replace('_', ' ')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">${tx.amount}</p>
                      <p className="text-xs text-zinc-400">
                        {new Date(tx.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
                {transactions.length === 0 && (
                  <p className="text-center text-zinc-500 py-4">No transactions yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <Card className="border-zinc-200">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row gap-4 justify-between">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                    data-testid="user-search"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Select value={filterTier} onValueChange={setFilterTier}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Filter by tier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tiers</SelectItem>
                      {tiers.map(tier => (
                        <SelectItem key={tier.id} value={tier.id}>{tier.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={() => { setCreateUserResult(null); setCreateUserOpen(true); }}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="admin-create-user-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New user
                  </Button>
                </div>
              </div>
            </CardHeader>
            <ScrollArea className="h-[500px]">
              <CardContent>
                <div className="space-y-2">
                  {filteredUsers.map((user) => (
                    <div 
                      key={user.id} 
                      className="flex items-center justify-between p-3 rounded-lg border border-zinc-100 hover:bg-zinc-50"
                      data-testid={`user-row-${user.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          user.is_admin ? 'bg-amber-100' : 'bg-indigo-100'
                        }`}>
                          {user.is_admin ? (
                            <Shield className="w-5 h-5 text-amber-600" />
                          ) : (
                            <Users className="w-5 h-5 text-indigo-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-zinc-900">{user.name}</p>
                            {user.is_admin && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">Admin</Badge>
                            )}
                            {user.role === 'support_staff' && !user.is_admin && (
                              <Badge className="bg-indigo-100 text-indigo-700 text-xs" data-testid={`support-staff-badge-${user.id}`}>Support</Badge>
                            )}
                            {user.is_banned && (
                              <Badge variant="destructive" className="text-xs">Banned</Badge>
                            )}
                            {internalTierIds.has(user.subscription_tier) && (
                              <Badge
                                className="bg-emerald-100 text-emerald-700 text-xs"
                                data-testid={`internal-plan-badge-${user.id}`}
                              >
                                {user.subscription_tier === 'platform_owner' ? 'Owner' : 'Staff'}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-zinc-500">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right hidden sm:block">
                          <Badge variant="outline" className="capitalize">
                            {user.subscription_tier?.replace('_', ' ')}
                          </Badge>
                          <p className="text-xs text-zinc-400 mt-1">
                            {user.posts_used_this_month || 0} posts
                          </p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => viewUserDetails(user.id)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setEditUserModal(user)}>
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit User
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleImpersonate(user)}
                              disabled={user.id === currentUser?.id}
                              data-testid={`admin-impersonate-${user.id}`}
                            >
                              <UserCog className="w-4 h-4 mr-2" />
                              Log in as this user
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleSendResetLink(user)}
                              data-testid={`admin-send-reset-link-${user.id}`}
                            >
                              <Mail className="w-4 h-4 mr-2" />
                              Email reset link
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setResetPwUser(user)}
                              data-testid={`admin-reset-pw-trigger-${user.id}`}
                            >
                              <KeyRound className="w-4 h-4 mr-2" />
                              Set / generate password
                            </DropdownMenuItem>
                            {!user.is_admin && (
                              <DropdownMenuItem
                                onClick={() => handleToggleBan(user)}
                                data-testid={`admin-toggle-ban-${user.id}`}
                                className={user.is_banned ? 'text-emerald-600' : 'text-amber-600'}
                              >
                                <Ban className="w-4 h-4 mr-2" />
                                {user.is_banned ? 'Enable account' : 'Disable account'}
                              </DropdownMenuItem>
                            )}
                            {!user.is_admin && (
                              <DropdownMenuItem 
                                onClick={() => handleDeleteUser(user.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete User
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </ScrollArea>
          </Card>
        </TabsContent>

        {/* Tickets Tab */}
        <TabsContent value="tickets" className="space-y-4">
          <AdminTicketsPanel />
        </TabsContent>

        {/* AI Support Chats Tab */}
        <TabsContent value="aichats" className="space-y-4">
          <AdminSupportChatsPanel api={api} />
        </TabsContent>

        {/* Tiers Tab */}
        <TabsContent value="tiers" className="space-y-4">
          <StripeSyncPanel api={api} />
          <div className="grid md:grid-cols-3 gap-4">
            {tiers.map((tier) => (
              <Card
                key={tier.id}
                className={`border-zinc-200 ${tier.archived ? 'opacity-60 bg-zinc-50' : ''}`}
                data-testid={`tier-card-${tier.id}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getTierIcon(tier.id)}
                      <CardTitle className="text-lg">{tier.name}</CardTitle>
                      {tier.internal && (
                        <Badge
                          className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs"
                          data-testid={`tier-internal-badge-${tier.id}`}
                        >
                          Internal · Hidden
                        </Badge>
                      )}
                      {tier.archived && (
                        <Badge
                          variant="secondary"
                          className="bg-zinc-200 text-zinc-700 text-xs"
                          data-testid={`tier-disabled-badge-${tier.id}`}
                        >
                          Disabled
                        </Badge>
                      )}
                      {tier.discount_active && (
                        <Badge
                          className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs gap-1"
                          data-testid={`tier-discount-badge-${tier.id}`}
                        >
                          <Sparkles className="w-3 h-3" />
                          {tier.discount_percent}% off
                        </Badge>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`tier-menu-${tier.id}`}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => setEditTierModal(tier)}
                          data-testid={`edit-tier-${tier.id}`}
                        >
                          <Edit2 className="w-4 h-4 mr-2" />
                          Edit plan
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleToggleTierArchived(tier)}
                          data-testid={`toggle-tier-archived-${tier.id}`}
                        >
                          {tier.archived ? (
                            <>
                              <Check className="w-4 h-4 mr-2 text-emerald-600" />
                              Enable plan
                            </>
                          ) : (
                            <>
                              <Ban className="w-4 h-4 mr-2 text-amber-600" />
                              Disable plan
                            </>
                          )}
                        </DropdownMenuItem>
                        {!DEFAULT_TIER_IDS.has(tier.id) && (
                          <DropdownMenuItem
                            onClick={() => handleDeleteTier(tier)}
                            className="text-red-600"
                            data-testid={`delete-tier-${tier.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete plan
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    {tier.discount_active && tier.discounted_price != null ? (
                      <>
                        <p className="text-3xl font-bold">
                          ${tier.discounted_price.toFixed(2)}
                          <span className="text-base font-normal text-zinc-500">/month</span>
                        </p>
                        <p className="text-xs text-zinc-500 line-through">
                          Was ${tier.price}/mo
                        </p>
                      </>
                    ) : (
                      <p className="text-3xl font-bold">
                        {tier.is_trial ? 'Free' : `$${tier.price}`}
                        {!tier.is_trial && <span className="text-base font-normal text-zinc-500">/month</span>}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Duration</span>
                      <span>{tier.duration_days} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Posts/Month</span>
                      <span>{tier.posts_per_month === -1 ? 'Unlimited' : tier.posts_per_month}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">AI Images/Month</span>
                      <span>
                        {tier.ai_images_per_month == null
                          ? '—'
                          : tier.ai_images_per_month === -1
                            ? 'Unlimited'
                            : tier.ai_images_per_month}
                      </span>
                    </div>
                  </div>
                  <div className="pt-3 border-t">
                    <p className="text-xs text-zinc-500 mb-2">Features:</p>
                    <div className="flex flex-wrap gap-1">
                      {tier.features?.map((f, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Features Tab — admin matrix to gate features per subscription tier */}
        <TabsContent value="features" className="space-y-4">
          <FeatureFlagsPanel />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <SiteSettingsSection />
          <Card className="border-zinc-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Stripe Configuration
                  </CardTitle>
                  <CardDescription>Configure your Stripe API keys for payments</CardDescription>
                </div>
                <Button onClick={() => setStripeSettingsModal(true)} data-testid="configure-stripe-btn">
                  <Settings className="w-4 h-4 mr-2" />
                  Configure
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-50">
                  <div>
                    <p className="font-medium">Secret Key</p>
                    <p className="text-sm text-zinc-500">
                      {settings?.stripe?.stripe_secret_key_masked || 'Not configured (using default)'}
                    </p>
                  </div>
                  {settings?.stripe?.stripe_secret_key_masked ? (
                    <Check className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <X className="w-5 h-5 text-zinc-300" />
                  )}
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-50">
                  <div>
                    <p className="font-medium">Webhook Secret</p>
                    <p className="text-sm text-zinc-500">
                      {settings?.stripe?.stripe_webhook_secret ? 'Configured' : 'Not configured'}
                    </p>
                  </div>
                  {settings?.stripe?.stripe_webhook_secret ? (
                    <Check className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <X className="w-5 h-5 text-zinc-300" />
                  )}
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-50">
                  <div>
                    <p className="font-medium">Connect Client ID <span className="text-xs text-zinc-400">(client payments)</span></p>
                    <p className="text-sm text-zinc-500">
                      {settings?.stripe?.connect_client_id_set ? `Configured${settings?.stripe?.secret_key_mode ? ` · ${settings.stripe.secret_key_mode} mode` : ''}` : 'Not configured'}
                    </p>
                  </div>
                  {settings?.stripe?.connect_client_id_set ? (
                    <Check className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <X className="w-5 h-5 text-zinc-300" />
                  )}
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-50">
                  <div>
                    <p className="font-medium">Connect Webhook Secret</p>
                    <p className="text-sm text-zinc-500">
                      {settings?.stripe?.connect_webhook_secret_set ? 'Configured' : 'Not configured (polling covers status)'}
                    </p>
                  </div>
                  {settings?.stripe?.connect_webhook_secret_set ? (
                    <Check className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <X className="w-5 h-5 text-zinc-300" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* OpenAI Settings Card */}
          <Card data-testid="openai-settings-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-emerald-600" />
                    OpenAI API Key (Image Generation)
                  </CardTitle>
                  <CardDescription>
                    Use your own OpenAI key for premium image models. Falls back to the Emergent Universal Key when unset.
                  </CardDescription>
                </div>
                <Button onClick={() => setOpenaiSettingsModal(true)} data-testid="configure-openai-btn">
                  <Settings className="w-4 h-4 mr-2" />
                  Configure
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-50">
                  <div>
                    <p className="font-medium">OpenAI API Key</p>
                    <p className="text-sm text-zinc-500" data-testid="openai-key-status">
                      {settings?.openai?.openai_api_key_masked ||
                        (settings?.openai?.configured
                          ? 'Configured'
                          : 'Not set — using Emergent Universal Key (gpt-image-1 only)')}
                    </p>
                  </div>
                  {settings?.openai?.configured ? (
                    <Check className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <X className="w-5 h-5 text-zinc-300" />
                  )}
                </div>
                <p className="text-xs text-zinc-500 leading-relaxed">
                  With a personal key, you unlock every OpenAI image model your account supports —
                  including higher-resolution variants and any models the Universal Key doesn't carry
                  yet (e.g. gpt-image-2). The key is stored server-side and used only for image generation.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* SendGrid email settings */}
          <SendGridSettingsPanel api={api} adminEmail={currentUser?.email} />

          {/* Image credit weighting */}
          <ImageCreditWeightsPanel api={api} />
        </TabsContent>

        {/* Platform Connections Tab */}
        <TabsContent value="connections" className="space-y-4">
          <PlatformCredentialsManager />
        </TabsContent>

        <TabsContent value="sso" className="space-y-4">
          <SSOApplicationsSection />
        </TabsContent>

        <TabsContent value="deploy" className="space-y-4">
          <DeploymentCheckPanel />
          <RescueScheduledMediaPanel api={api} />
        </TabsContent>
      </Tabs>
      <Dialog open={!!editUserModal} onOpenChange={() => setEditUserModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user subscription and status</DialogDescription>
          </DialogHeader>
          {editUserModal && (
            <div className="space-y-4">
              <div>
                <p className="font-medium">{editUserModal.name}</p>
                <p className="text-sm text-zinc-500">{editUserModal.email}</p>
              </div>
              <div className="space-y-2">
                <Label>Subscription Tier</Label>
                <Select 
                  value={editUserModal.subscription_tier} 
                  onValueChange={(v) => setEditUserModal({...editUserModal, subscription_tier: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tiers.map(tier => (
                      <SelectItem key={tier.id} value={tier.id}>{tier.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Subscription Status</Label>
                <Select 
                  value={editUserModal.subscription_status} 
                  onValueChange={(v) => setEditUserModal({...editUserModal, subscription_status: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label>Banned</Label>
                <Switch 
                  checked={editUserModal.is_banned} 
                  onCheckedChange={(v) => setEditUserModal({...editUserModal, is_banned: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Support Staff</Label>
                  <p className="text-xs text-zinc-500">Allows viewing & replying to all support tickets.</p>
                </div>
                <Switch
                  checked={editUserModal.role === 'support_staff'}
                  onCheckedChange={(v) => setEditUserModal({ ...editUserModal, role: v ? 'support_staff' : '' })}
                  data-testid="toggle-support-staff"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserModal(null)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleUpdateUser}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tier Modal — the form is long (name, price, limits, promo trial,
          features list, etc.). On mobile the dialog used to overflow the
          viewport so the Save button was unreachable; on desktop it grew
          taller than the screen. Cap height to 90vh, scroll the body, pin the
          footer so Save is always visible. */}
      <Dialog open={!!editTierModal} onOpenChange={() => setEditTierModal(null)}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden"
          data-testid="edit-tier-modal"
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-100 shrink-0">
            <DialogTitle>Edit Pricing Tier</DialogTitle>
            <DialogDescription>Update tier pricing and limits</DialogDescription>
          </DialogHeader>
          {editTierModal && (
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="edit-tier-modal-body">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input 
                  value={editTierModal.name} 
                  onChange={(e) => setEditTierModal({...editTierModal, name: e.target.value})}
                />
              </div>
              {!editTierModal.is_trial && (
                <div className="space-y-2">
                  <Label>Price (USD/month)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={editTierModal.price} 
                    onChange={(e) => setEditTierModal({...editTierModal, price: e.target.value})}
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Duration (days)</Label>
                <Input 
                  type="number"
                  value={editTierModal.duration_days} 
                  onChange={(e) => setEditTierModal({...editTierModal, duration_days: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Posts per Month (number, or "Unlimited")</Label>
                <Input
                  type="text"
                  value={editTierModal.posts_per_month === -1 ? 'Unlimited' : editTierModal.posts_per_month}
                  onChange={(e) => setEditTierModal({...editTierModal, posts_per_month: e.target.value})}
                  data-testid="edit-tier-ppm"
                />
                <p className="text-xs text-zinc-500">Type a positive number, or "Unlimited" (saved as -1 in DB).</p>
              </div>
              <div className="space-y-2">
                <Label>AI Images per Month (number, or "Unlimited")</Label>
                <Input
                  type="text"
                  value={
                    editTierModal.ai_images_per_month === -1
                      ? 'Unlimited'
                      : (editTierModal.ai_images_per_month ?? '')
                  }
                  onChange={(e) => setEditTierModal({...editTierModal, ai_images_per_month: e.target.value})}
                  placeholder="e.g. 800"
                  data-testid="edit-tier-aipm"
                />
                <p className="text-xs text-zinc-500">
                  Gates AI image generation per user. Leave blank to keep current cap.
                  "Unlimited" = no cap (saved as -1).
                </p>
              </div>

              {/* ── Promotional discount ─────────────────────────────────── */}
              <div className="space-y-3 rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                <div>
                  <Label className="text-sm font-semibold flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                    Promotional discount
                  </Label>
                  <p className="text-[11px] text-zinc-500 mt-1">
                    Applies to NEW signups only. Existing subscribers keep their current rate.
                    Auto-creates a Stripe Coupon and attaches it at checkout.
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Discount %</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={editTierModal.discount_percent ?? ''}
                      onChange={(e) =>
                        setEditTierModal({ ...editTierModal, discount_percent: e.target.value })
                      }
                      placeholder="0 = no discount"
                      data-testid="edit-tier-discount-pct"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Marketing label (optional)</Label>
                    <Input
                      type="text"
                      maxLength={40}
                      value={editTierModal.discount_label ?? ''}
                      onChange={(e) =>
                        setEditTierModal({ ...editTierModal, discount_label: e.target.value })
                      }
                      placeholder="e.g. Launch deal"
                      data-testid="edit-tier-discount-label"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Ends at (optional — leave blank for forever)</Label>
                  <Input
                    type="datetime-local"
                    value={
                      // Render ISO → datetime-local: strip seconds/timezone
                      (editTierModal.discount_ends_at || '').slice(0, 16)
                    }
                    onChange={(e) => {
                      // datetime-local → ISO with timezone
                      const v = e.target.value;
                      setEditTierModal({
                        ...editTierModal,
                        discount_ends_at: v ? new Date(v).toISOString() : '',
                      });
                    }}
                    data-testid="edit-tier-discount-ends-at"
                  />
                </div>
                {/* Live preview */}
                {editTierModal.discount_percent > 0 && editTierModal.price > 0 && (
                  <div className="text-xs bg-white border border-zinc-200 rounded-md p-2">
                    <span className="text-zinc-500 line-through mr-2">
                      ${Number(editTierModal.price).toFixed(2)}
                    </span>
                    <span className="font-semibold text-emerald-700">
                      ${(
                        Number(editTierModal.price) *
                        (100 - Number(editTierModal.discount_percent)) /
                        100
                      ).toFixed(2)}
                      /mo
                    </span>
                    <span className="text-zinc-500 ml-2">
                      Save {Number(editTierModal.discount_percent)}%
                      {editTierModal.discount_label ? ` — ${editTierModal.discount_label}` : ''}
                    </span>
                  </div>
                )}
              </div>

              {/* $1 / N-day promo trial (Stripe Subscription Schedule under the hood) */}
              <div className="space-y-3 border-t border-zinc-200 pt-4 bg-amber-50/40 -mx-1 px-4 py-3 rounded-md" data-testid="promo-trial-section">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-semibold">Intro Promo Trial</Label>
                    <p className="text-xs text-zinc-500">
                      Charge $1 for the first N days, then Stripe auto-bills the regular price.
                      Only offered to brand-new users (never paid before). Tight post caps prevent
                      a user from generating a month of content in the cheap window.
                    </p>
                  </div>
                  <Switch
                    checked={!!editTierModal.promo_trial_enabled}
                    onCheckedChange={(v) =>
                      setEditTierModal({ ...editTierModal, promo_trial_enabled: v })
                    }
                    data-testid="promo-trial-toggle"
                  />
                </div>
                {editTierModal.promo_trial_enabled && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Trial price ($)</Label>
                      <Input
                        type="number"
                        min="0.50"
                        step="0.50"
                        value={editTierModal.promo_trial_price ?? 1}
                        onChange={(e) =>
                          setEditTierModal({ ...editTierModal, promo_trial_price: Number(e.target.value) })
                        }
                        data-testid="promo-trial-price"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Days</Label>
                      <Input
                        type="number"
                        min="1"
                        max="30"
                        value={editTierModal.promo_trial_days ?? 5}
                        onChange={(e) =>
                          setEditTierModal({ ...editTierModal, promo_trial_days: Number(e.target.value) })
                        }
                        data-testid="promo-trial-days"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Max posts generated</Label>
                      <Input
                        type="number"
                        min="1"
                        max="500"
                        value={editTierModal.promo_trial_post_cap ?? 10}
                        onChange={(e) =>
                          setEditTierModal({ ...editTierModal, promo_trial_post_cap: Number(e.target.value) })
                        }
                        data-testid="promo-trial-post-cap"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Max scheduled future-posts</Label>
                      <Input
                        type="number"
                        min="1"
                        max="500"
                        value={editTierModal.promo_trial_scheduled_cap ?? 10}
                        onChange={(e) =>
                          setEditTierModal({ ...editTierModal, promo_trial_scheduled_cap: Number(e.target.value) })
                        }
                        data-testid="promo-trial-scheduled-cap"
                      />
                    </div>
                  </div>
                )}
                {editTierModal.promo_trial_enabled && (
                  <p className="text-xs text-amber-800 bg-amber-100/60 rounded px-2 py-1">
                    Preview: ${(editTierModal.promo_trial_price ?? 1).toFixed(2)} for first {editTierModal.promo_trial_days ?? 5} days,
                    then ${Number(editTierModal.price || 0).toFixed(2)}/mo auto-billed.
                  </p>
                )}
              </div>

              {/* Plan features editor — add / edit / reorder / remove */}
              <div className="space-y-2 border-t border-zinc-200 pt-4">
                <div className="flex items-center justify-between">
                  <Label>Plan features</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addFeature}
                    data-testid="add-feature-btn"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    Add feature
                  </Button>
                </div>
                {(!editTierModal.features || editTierModal.features.length === 0) ? (
                  <p className="text-xs text-zinc-500 italic px-2 py-3 border border-dashed border-zinc-200 rounded-md text-center">
                    No features yet. Click "Add feature" to start.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {editTierModal.features.map((feature, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2"
                        data-testid={`feature-row-${idx}`}
                      >
                        <GripVertical className="w-4 h-4 text-zinc-300 flex-shrink-0" />
                        <Input
                          value={feature}
                          placeholder="e.g. 100 posts per month"
                          onChange={(e) => updateFeature(idx, e.target.value)}
                          className="flex-1"
                          data-testid={`feature-input-${idx}`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveFeature(idx, -1)}
                          disabled={idx === 0}
                          className="h-9 w-9 p-0"
                          data-testid={`feature-up-${idx}`}
                          title="Move up"
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveFeature(idx, 1)}
                          disabled={idx === editTierModal.features.length - 1}
                          className="h-9 w-9 p-0"
                          data-testid={`feature-down-${idx}`}
                          title="Move down"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFeature(idx)}
                          className="h-9 w-9 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                          data-testid={`feature-remove-${idx}`}
                          title="Remove"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-zinc-500">
                  Empty rows are dropped on save. Order here is the order shown to users.
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="px-6 py-3 border-t border-zinc-100 bg-white shrink-0">
            <Button variant="outline" onClick={() => setEditTierModal(null)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleUpdateTier} data-testid="tier-save-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Reset Password Modal */}
      <Dialog open={!!resetPwUser} onOpenChange={(open) => !open && closeResetPwModal()}>
        <DialogContent className="max-w-md" data-testid="admin-reset-pw-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-indigo-600" />
              Reset user password
            </DialogTitle>
            <DialogDescription>
              {resetPwUser ? <>For <span className="font-medium">{resetPwUser.email}</span></> : null}
            </DialogDescription>
          </DialogHeader>

          {!resetPwResult ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>New password</Label>
                <Input
                  type="text"
                  placeholder="Leave blank to auto-generate a temporary password"
                  value={resetPwForm.new_password}
                  onChange={(e) => setResetPwForm({ ...resetPwForm, new_password: e.target.value })}
                  data-testid="admin-reset-pw-input"
                />
                <p className="text-xs text-zinc-500">Min 8 characters. Leave blank for a secure 12-char temp password.</p>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border border-zinc-200">
                <div>
                  <p className="text-sm font-medium">Email new password to user</p>
                  <p className="text-xs text-zinc-500">Sends via SendGrid to {resetPwUser?.email}</p>
                </div>
                <Switch
                  checked={resetPwForm.send_email}
                  onCheckedChange={(v) => setResetPwForm({ ...resetPwForm, send_email: v })}
                  data-testid="admin-reset-pw-email-toggle"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeResetPwModal} disabled={resetPwLoading}>
                  Cancel
                </Button>
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleAdminResetPassword}
                  disabled={resetPwLoading || (resetPwForm.new_password && resetPwForm.new_password.length < 8)}
                  data-testid="admin-reset-pw-submit"
                >
                  {resetPwLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Reset password
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4" data-testid="admin-reset-pw-result">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <div className="flex items-center gap-2 text-emerald-700 text-sm font-medium">
                  <Check className="w-4 h-4" />
                  Password reset successfully
                </div>
                <p className="text-xs text-emerald-700 mt-1">
                  {resetPwResult.email_sent
                    ? `An email with the new password was sent to ${resetPwResult.user_email}.`
                    : 'Email was not sent. Share the password below securely with the user.'}
                </p>
              </div>
              <div className="space-y-2">
                <Label>{resetPwResult.was_generated ? 'Temporary password' : 'New password'}</Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={resetPwResult.new_password}
                    readOnly
                    className="font-mono"
                    data-testid="admin-reset-pw-result-value"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(resetPwResult.new_password);
                      toast.success('Copied');
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-zinc-500">
                  This password is shown only once. The user should change it after signing in.
                </p>
              </div>
              <DialogFooter>
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={closeResetPwModal}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View User Modal */}
      <Dialog open={!!viewUserModal} onOpenChange={() => setViewUserModal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {viewUserModal && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-zinc-500">Name</p>
                  <p className="font-medium">{viewUserModal.user.name}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Email</p>
                  <p className="font-medium">{viewUserModal.user.email}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Subscription</p>
                  <Badge variant="outline" className="capitalize">
                    {viewUserModal.user.subscription_tier?.replace('_', ' ')}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Posts Created</p>
                  <p className="font-medium">{viewUserModal.posts_count}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Joined</p>
                  <p className="font-medium">
                    {new Date(viewUserModal.user.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Posts This Month</p>
                  <p className="font-medium">{viewUserModal.user.posts_used_this_month || 0}</p>
                </div>
              </div>
              {viewUserModal.payments.length > 0 && (
                <div>
                  <p className="text-sm text-zinc-500 mb-2">Payment History</p>
                  <div className="space-y-2">
                    {viewUserModal.payments.map((payment) => (
                      <div key={payment.id} className="flex justify-between p-2 bg-zinc-50 rounded">
                        <span className="capitalize">{payment.tier_id?.replace('_', ' ')}</span>
                        <span className="font-medium">${payment.amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Stripe Settings Modal */}
      <StripeSettingsModal 
        open={stripeSettingsModal}
        onClose={() => setStripeSettingsModal(false)}
        onSave={handleSaveStripeSettings}
        currentSettings={settings?.stripe}
      />

      {/* OpenAI Settings Modal */}
      <OpenAISettingsModal
        open={openaiSettingsModal}
        onClose={() => setOpenaiSettingsModal(false)}
        onSave={handleSaveOpenAISettings}
        onVerify={handleVerifyOpenAI}
        currentSettings={settings?.openai}
      />

      {/* Create User Modal */}
      <CreateUserModal
        open={createUserOpen}
        onClose={() => { setCreateUserOpen(false); setCreateUserResult(null); }}
        onCreate={handleCreateUser}
        tiers={tiers}
        result={createUserResult}
      />
    </div>
  );
};

const CreateUserModal = ({ open, onClose, onCreate, tiers, result }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [tier, setTier] = useState('trial');
  const [mode, setMode] = useState('generate'); // 'generate' | 'manual' | 'reset_link'
  const [submitting, setSubmitting] = useState(false);

  // Reset fields when modal closes
  const handleClose = () => {
    setEmail(''); setName(''); setPassword(''); setTier('trial'); setMode('generate');
    onClose();
  };

  const submit = async () => {
    if (!email.trim()) return;
    setSubmitting(true);
    const payload = {
      email: email.trim(),
      name: name.trim() || null,
      subscription_tier: tier,
    };
    if (mode === 'manual') {
      if (!password || password.length < 8) {
        setSubmitting(false);
        return;
      }
      payload.password = password;
    } else if (mode === 'reset_link') {
      payload.send_reset_link = true;
    }
    await onCreate(payload);
    setSubmitting(false);
  };

  // Copy generated password on demand
  const copyPw = () => {
    navigator.clipboard.writeText(result.generated_password);
    toast.success('Password copied');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent data-testid="admin-create-user-modal">
        <DialogHeader>
          <DialogTitle>Create user</DialogTitle>
          <DialogDescription>
            Add a new account. Email is required. Pick how the user will sign in for the first time.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          // Result panel — shown after successful create
          <div className="space-y-4" data-testid="create-user-result">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <p className="font-medium text-emerald-900">
                User created: {result.user?.email}
              </p>
              {result.generated_password && (
                <div className="mt-3">
                  <Label>Temporary password (share this once)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input readOnly value={result.generated_password} className="font-mono text-sm" data-testid="temp-password" />
                    <Button variant="outline" size="icon" onClick={copyPw}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-emerald-800 mt-1">
                    For security, ask the user to change this on first login.
                  </p>
                </div>
              )}
              {result.reset_link_sent && (
                <p className="text-sm text-emerald-800 mt-2">
                  Password-set email sent. The user will pick their own password via the link.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button onClick={handleClose} data-testid="create-user-close-btn">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          // Form
          <div className="space-y-4">
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@company.com"
                autoFocus
                data-testid="create-user-email"
              />
            </div>
            <div>
              <Label>Name (optional)</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                data-testid="create-user-name"
              />
            </div>
            <div>
              <Label>Subscription tier</Label>
              <Select value={tier} onValueChange={setTier}>
                <SelectTrigger data-testid="create-user-tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tiers.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Password setup</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="create-user-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="generate">Auto-generate (I'll share it)</SelectItem>
                  <SelectItem value="reset_link">Email the user a set-password link</SelectItem>
                  <SelectItem value="manual">I'll set the password now</SelectItem>
                </SelectContent>
              </Select>
              {mode === 'manual' && (
                <Input
                  type="password"
                  className="mt-2"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 8 characters"
                  data-testid="create-user-password"
                />
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={submit}
                disabled={submitting || !email.trim() || (mode === 'manual' && password.length < 8)}
                data-testid="create-user-submit-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Create user
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const OpenAISettingsModal = ({ open, onClose, onSave, onVerify, currentSettings }) => {
  const [key, setKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const submit = async () => {
    if (!key.trim()) return;
    setSaving(true);
    await onSave({ key: key.trim() });
    setSaving(false);
    setKey('');
  };

  const clear = async () => {
    if (!window.confirm('Clear the OpenAI API key? Image generation will fall back to the Emergent Universal Key.')) return;
    setSaving(true);
    await onSave({ clear: true });
    setSaving(false);
  };

  const verify = async () => {
    setVerifying(true);
    await onVerify();
    setVerifying(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="openai-settings-modal">
        <DialogHeader>
          <DialogTitle>OpenAI API Key</DialogTitle>
          <DialogDescription>
            Used for image generation (gpt-image-1, dall-e-3, gpt-image-2 if your account supports it).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>API Key</Label>
            <Input
              type="password"
              placeholder={currentSettings?.openai_api_key_masked || 'sk-...'}
              value={key}
              onChange={(e) => setKey(e.target.value)}
              data-testid="openai-key-input"
            />
            <p className="text-xs text-zinc-500">
              Get your key at{' '}
              <a
                href="https://platform.openai.com/api-keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-700 underline"
              >
                platform.openai.com/api-keys
              </a>
              . The key is sent over HTTPS and stored server-side.
            </p>
          </div>
          {currentSettings?.configured && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-100">
              <div className="text-sm">
                <p className="font-medium text-emerald-900">Currently configured</p>
                <p className="text-emerald-700 text-xs">{currentSettings.openai_api_key_masked}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={verify}
                  disabled={verifying}
                  data-testid="openai-verify-btn"
                >
                  {verifying && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                  Test
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clear}
                  disabled={saving}
                  className="text-red-600 hover:text-red-700"
                  data-testid="openai-clear-btn"
                >
                  Clear
                </Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={submit}
            disabled={saving || !key.trim()}
            data-testid="openai-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Save key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StripeSettingsModal = ({ open, onClose, onSave, currentSettings }) => {
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [connectClientId, setConnectClientId] = useState('');
  const [connectWebhookSecret, setConnectWebhookSecret] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave({
      stripe_secret_key: secretKey || undefined,
      stripe_webhook_secret: webhookSecret || undefined,
      stripe_publishable_key: publishableKey || undefined,
      stripe_connect_client_id: connectClientId || undefined,
      stripe_connect_webhook_secret: connectWebhookSecret || undefined,
    });
    setSaving(false);
    setSecretKey('');
    setWebhookSecret('');
    setPublishableKey('');
    setConnectClientId('');
    setConnectWebhookSecret('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Stripe Settings</DialogTitle>
          <DialogDescription>
            Configure your Stripe API keys. Leave empty to keep current values.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Secret Key (sk_...)</Label>
            <Input 
              type="password"
              placeholder={currentSettings?.stripe_secret_key_masked || 'Enter secret key'}
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Publishable Key (pk_...)</Label>
            <Input 
              placeholder="Enter publishable key"
              value={publishableKey}
              onChange={(e) => setPublishableKey(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Webhook Secret (whsec_...)</Label>
            <Input 
              type="password"
              placeholder="Enter webhook secret"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
            />
          </div>

          <div className="pt-3 mt-1 border-t border-zinc-200">
            <p className="text-sm font-semibold text-zinc-800">Connect — Client Payments</p>
            <p className="text-xs text-zinc-500 mb-3">Used when organizations connect their own Stripe account to bill clients (direct charges). Must be the SAME mode (test/live) as the secret key above.</p>
            <div className="space-y-2">
              <Label>Connect Client ID (ca_...)</Label>
              <Input
                placeholder={currentSettings?.connect_client_id_set ? '•••• configured' : 'Stripe Dashboard → Connect → Settings → OAuth'}
                value={connectClientId}
                onChange={(e) => setConnectClientId(e.target.value)}
                data-testid="stripe-connect-client-id-input"
              />
            </div>
            <div className="space-y-2 mt-3">
              <Label>Connect Webhook Secret (whsec_...)</Label>
              <Input
                type="password"
                placeholder={currentSettings?.connect_webhook_secret_set ? '•••• configured' : 'Connect webhook signing secret'}
                value={connectWebhookSecret}
                onChange={(e) => setConnectWebhookSecret(e.target.value)}
                data-testid="stripe-connect-webhook-secret-input"
              />
            </div>
          </div>

          <p className="text-xs text-zinc-500">
            Get your API keys from the Stripe Dashboard at stripe.com/dashboard/apikeys
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700" 
            onClick={handleSave}
            disabled={saving}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};



// ===================== Admin Tickets Panel =====================

const ADMIN_TICKET_CATEGORIES = {
  technical_support: 'Technical Support',
  feature_request:   'Feature Request',
  bug_report:        'Bug Report',
  billing:           'Billing & Payments',
  account_issues:    'Account Issues',
  general_support:   'General Support',
};

const ADMIN_STATUS_STYLES = {
  open:              { label: 'Open',           color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  in_progress:       { label: 'In Progress',    color: 'bg-blue-50 text-blue-700 border-blue-200' },
  waiting_on_user:   { label: 'Waiting on User',color: 'bg-amber-50 text-amber-700 border-amber-200' },
  resolved:          { label: 'Resolved',       color: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
  closed:            { label: 'Closed',         color: 'bg-zinc-100 text-zinc-500 border-zinc-200' },
};

const ADMIN_PRIORITY_STYLES = {
  low:      'bg-zinc-100 text-zinc-700',
  medium:   'bg-blue-50 text-blue-700',
  high:     'bg-amber-50 text-amber-700',
  critical: 'bg-red-50 text-red-700',
};

const fmtTicketDate = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const AdminTicketsPanel = () => {
  const { api } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTicketId, setActiveTicketId] = useState(null);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const r = await api.get('/support/tickets');
      setTickets(r.data?.tickets || []);
    } catch (_) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, []); // eslint-disable-line

  const filtered = tickets.filter((t) => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      if (!t.subject.toLowerCase().includes(s) &&
          !(t.user_email || '').toLowerCase().includes(s) &&
          !String(t.number).includes(s)) {
        return false;
      }
    }
    return true;
  });

  const stats = {
    total: tickets.length,
    open: tickets.filter((t) => t.status === 'open').length,
    inProgress: tickets.filter((t) => t.status === 'in_progress').length,
    awaitingUser: tickets.filter((t) => t.status === 'waiting_on_user').length,
    resolved: tickets.filter((t) => ['resolved', 'closed'].includes(t.status)).length,
  };

  return (
    <div className="space-y-4" data-testid="admin-tickets-panel">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total', value: stats.total, color: 'text-zinc-900' },
          { label: 'Open', value: stats.open, color: 'text-emerald-600' },
          { label: 'In Progress', value: stats.inProgress, color: 'text-blue-600' },
          { label: 'Awaiting User', value: stats.awaitingUser, color: 'text-amber-600' },
          { label: 'Resolved/Closed', value: stats.resolved, color: 'text-zinc-500' },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                placeholder="Search ticket #, subject, or email…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="admin-tickets-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="admin-tickets-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(ADMIN_STATUS_STYLES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger data-testid="admin-tickets-category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {Object.entries(ADMIN_TICKET_CATEGORIES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-zinc-500">{filtered.length} of {tickets.length} tickets</p>
            <Button variant="outline" size="sm" onClick={fetchTickets}>
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tickets list */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-zinc-500">
            <LifeBuoy className="w-10 h-10 mx-auto text-zinc-300 mb-2" />
            No tickets match the current filters.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="admin-tickets-list">
          {filtered.map((t) => {
            const st = ADMIN_STATUS_STYLES[t.status] || ADMIN_STATUS_STYLES.open;
            return (
              <Card
                key={t.id}
                onClick={() => setActiveTicketId(t.id)}
                className="cursor-pointer hover:border-indigo-300 transition"
                data-testid={`admin-ticket-row-${t.id}`}
              >
                <CardContent className="p-4 flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-mono text-zinc-400">#{t.number}</span>
                      <h3 className="font-medium text-zinc-900 truncate">{t.subject}</h3>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap text-xs text-zinc-500">
                      <span>From: <span className="text-zinc-700">{t.user_email}</span></span>
                      <span>·</span>
                      <span>{ADMIN_TICKET_CATEGORIES[t.category] || t.category}</span>
                      <span>·</span>
                      <span>Updated {fmtTicketDate(t.updated_at)}</span>
                      {t.messages?.length > 0 && (
                        <>
                          <span>·</span>
                          <span>{t.messages.length} {t.messages.length === 1 ? 'reply' : 'replies'}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <Badge variant="outline" className={st.color}>{st.label}</Badge>
                    <span className={`text-xs px-2 py-0.5 rounded ${ADMIN_PRIORITY_STYLES[t.priority] || ''}`}>
                      {t.priority}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {activeTicketId && (
        <TicketDetailDialog
          ticketId={activeTicketId}
          isSupportStaff={true}
          onClose={() => { setActiveTicketId(null); fetchTickets(); }}
        />
      )}
    </div>
  );
};
